const API_BASE = (import.meta.env.VITE_API_BASE || '').replace(/\/+$/, '')
const API_TOKEN = (import.meta.env.VITE_SUPPORT_PORTAL_TOKEN || '').trim()

export function apiConfigured() {
  return API_BASE.length > 0
}

export function apiBase() {
  return API_BASE
}

/**
 * Cliente API → ITManager `/api/v1/support-portal`.
 * @param {string} path
 * @param {RequestInit & { json?: unknown }} [opts]
 */
export async function api(path, opts = {}) {
  if (!API_BASE) {
    throw new Error('Servicio no disponible')
  }
  const headers = {
    Accept: 'application/json',
    ...(opts.headers || {}),
  }
  if (API_TOKEN) {
    headers['X-Support-Portal-Token'] = API_TOKEN
  }
  if (opts.clientAuth) {
    const { getClientToken } = await import('./clientAuth.js')
    const bearer = getClientToken()
    if (bearer) headers.Authorization = `Bearer ${bearer}`
  }
  let body = opts.body
  if (opts.json !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(opts.json)
  }
  // FormData: no fijar Content-Type (boundary automático)
  if (body instanceof FormData) {
    delete headers['Content-Type']
  }
  const res = await fetch(`${API_BASE}${path.startsWith('/') ? path : `/${path}`}`, {
    ...opts,
    headers,
    body,
  })
  const text = await res.text()
  let data = null
  try {
    data = text ? JSON.parse(text) : null
  } catch {
    data = { raw: text }
  }
  if (!res.ok) {
    const msg = data?.message || data?.error || `HTTP ${res.status}`
    const err = new Error(msg)
    err.status = res.status
    err.data = data
    throw err
  }
  return data
}

export async function pingHealth() {
  if (!API_BASE) return { ok: false, reason: 'no_api_base' }
  try {
    const data = await api('/health')
    return { ok: true, data }
  } catch (e) {
    return { ok: false, reason: e.message, status: e.status }
  }
}

export async function fetchConfig(company) {
  const q = company ? `?company=${encodeURIComponent(company)}` : ''
  return api(`/config${q}`)
}

export async function createTicket(payloadOrFormData) {
  if (payloadOrFormData instanceof FormData) {
    return api('/tickets', { method: 'POST', body: payloadOrFormData })
  }
  return api('/tickets', { method: 'POST', json: payloadOrFormData })
}

export async function lookupTicket(ticket_number, requester_email) {
  return api('/tickets/lookup', {
    method: 'POST',
    json: { ticket_number, requester_email },
  })
}

export async function getTicket(token) {
  return api(`/tickets/${encodeURIComponent(token)}`)
}

export async function replyTicket(token, bodyOrFormData) {
  if (bodyOrFormData instanceof FormData) {
    return api(`/tickets/${encodeURIComponent(token)}/replies`, {
      method: 'POST',
      body: bodyOrFormData,
    })
  }
  return api(`/tickets/${encodeURIComponent(token)}/replies`, {
    method: 'POST',
    json: { body: bodyOrFormData },
  })
}

export async function submitCsat(token, csat_rating, csat_comment) {
  return api(`/tickets/${encodeURIComponent(token)}/csat`, {
    method: 'POST',
    json: { csat_rating, csat_comment },
  })
}

export async function pushSubscribe(token, subscriptionJson) {
  return api(`/tickets/${encodeURIComponent(token)}/push-subscribe`, {
    method: 'POST',
    json: subscriptionJson,
  })
}

export async function pushUnsubscribe(token, endpoint) {
  return api(`/tickets/${encodeURIComponent(token)}/push-unsubscribe`, {
    method: 'POST',
    json: { endpoint },
  })
}

export async function pushTest(token) {
  return api(`/tickets/${encodeURIComponent(token)}/push-test`, {
    method: 'POST',
    json: {},
  })
}

/** Acepta invitación portal (?invite=TOKEN) — marca cliente y devuelve prefills. */
export async function acceptInvite(token) {
  return api(`/invite/${encodeURIComponent(token)}/accept`, { method: 'POST' })
}

export async function aiDraft(payload) {
  return api('/ai/draft', { method: 'POST', json: payload })
}

export async function clientActivate(invite_token, password, password_confirmation) {
  return api('/auth/activate', {
    method: 'POST',
    json: { invite_token, password, password_confirmation },
  })
}

export async function clientLogin(email, password) {
  return api('/auth/login', { method: 'POST', json: { email, password } })
}

/** Handoff staff ITManager (token un solo uso). Spec: ITM-PORTAL-CLIENT-ADMIN-SOT-01 */
export async function staffHandoffExchange(handoff) {
  return api('/auth/staff-handoff', { method: 'POST', json: { handoff } })
}

export async function clientLogout() {
  return api('/auth/logout', { method: 'POST', clientAuth: true, json: {} })
}

export async function clientMe() {
  return api('/auth/me', { clientAuth: true })
}

export async function clientDashboard(installationId) {
  const q = installationId ? `?installation_id=${encodeURIComponent(installationId)}` : ''
  return api(`/client/dashboard${q}`, { clientAuth: true })
}

export async function clientDigitalTwin(installationId) {
  const q = installationId ? `?installation_id=${encodeURIComponent(installationId)}` : ''
  return api(`/client/digital-twin${q}`, { clientAuth: true })
}

/** Alta ticket autenticado (solo subject + description). Spec: ITM-SUPPORT-CLIENT-TICKET-01 */
export async function clientCreateTicket(payload) {
  return api('/client/tickets', { method: 'POST', clientAuth: true, json: payload })
}

/** Alcances de solicitud presupuesto IT. Spec: ITM-PORTAL-IT-QUOTE-01 */
export async function clientItQuoteScopes() {
  return api('/client/it-quote/scopes', { clientAuth: true })
}

export async function clientItQuoteRequests(installationId) {
  const q = installationId ? `?installation_id=${encodeURIComponent(installationId)}` : ''
  return api(`/client/it-quote-requests${q}`, { clientAuth: true })
}

/** Alta solicitud presupuesto (JSON o FormData con adjuntos). */
export async function clientStoreItQuoteRequest(payloadOrFormData) {
  if (payloadOrFormData instanceof FormData) {
    return api('/client/it-quote-requests', {
      method: 'POST',
      clientAuth: true,
      body: payloadOrFormData,
    })
  }
  return api('/client/it-quote-requests', {
    method: 'POST',
    clientAuth: true,
    json: payloadOrFormData,
  })
}

/** Documentos legales/SEPA (pendientes + firmados). Spec: WAVE-426 + WAVE-437 */
export async function clientPendingSignatures(installationId) {
  const q = installationId ? `?installation_id=${encodeURIComponent(installationId)}` : ''
  return api(`/client/pending-signatures${q}`, { clientAuth: true })
}

export async function clientNotices(installationId) {
  const q = installationId ? `?installation_id=${encodeURIComponent(installationId)}` : ''
  return api(`/client/notices${q}`, { clientAuth: true })
}

export async function clientAckNotice(noticeId, installationId) {
  const q = installationId ? `?installation_id=${encodeURIComponent(installationId)}` : ''
  return api(`/client/notices/${encodeURIComponent(noticeId)}/acknowledge${q}`, {
    method: 'POST',
    clientAuth: true,
    json: {},
  })
}

export async function clientOpenTickets(installationId) {
  const q = installationId ? `?installation_id=${encodeURIComponent(installationId)}` : ''
  return api(`/client/open-tickets${q}`, { clientAuth: true })
}

export async function clientNavCounts(installationId) {
  const q = installationId ? `?installation_id=${encodeURIComponent(installationId)}` : ''
  return api(`/client/nav-counts${q}`, { clientAuth: true })
}

/** PDF sellado vía API portal (Bearer). path = pdf_view_path | pdf_download_path */
export async function fetchClientSigningPdf(path, installationId) {
  const { getClientToken } = await import('./clientAuth.js')
  const bearer = getClientToken()
  if (!API_BASE || !bearer) {
    throw new Error('Sesión no válida')
  }
  let url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`
  if (installationId) {
    url += (url.includes('?') ? '&' : '?') + `installation_id=${encodeURIComponent(installationId)}`
  }
  const headers = { Accept: 'application/pdf', Authorization: `Bearer ${bearer}` }
  if (API_TOKEN) headers['X-Support-Portal-Token'] = API_TOKEN
  const res = await fetch(url, { headers })
  if (!res.ok) {
    let message = `HTTP ${res.status}`
    try {
      const data = await res.json()
      message = data?.message || message
    } catch (_) {}
    throw new Error(message)
  }
  return res.blob()
}
